import { Storage } from 'aws-amplify'
import uuidv4 from 'uuid/v4'
import config from '../../config/config'
import { asyncForEach, asyncReturnEach } from '../../libs/async'
import { Apartment } from '../../types/apartments'
import {
  ApartmentsBucketList,
  BucketObject,
  UploadFileToS3Response
} from './types'

// https://aws.github.io/aws-amplify/media/storage_guide
// https://github.com/aws/aws-amplify/blob/master/docs/media/storage_guide.md

Storage.configure({ level: 'public' })

const customPrefix = {
  public: '',
  protected: 'myProtectedPrefix/',
  private: 'myPrivatePrefix/'
}

const uploadApartmentImageFileToS3 = async (
  file: any,
  apartmentId: string
): Promise<UploadFileToS3Response> => {
  const filename = file.name
  const fileExtension = filename.split('.').pop()
  const generatedFilename = `${uuidv4()}.${fileExtension}`
  const key = `apartments/${apartmentId}/${generatedFilename}`
  try {
    await Storage.vault.put(key, file, {
      bucket: config.s3.transcodedMedia.BUCKET,
      region: config.s3.transcodedMedia.REGION,
      level: 'public',
      customPrefix,
      contentType: file.type
    })
    return {
      filename,
      key
    }
  } catch (error) {
    throw new Error(`Failed to upload ${file} to storage`)
  }
}

const uploadFileToS3 = async (
  file: any,
  bucket: string,
  region: string
): Promise<any> => {
  const fileExtension = file.type.split("/")[1];
  const generatedFilename = `${uuidv4()}.${fileExtension}`;
  const key = `profilePictures/${generatedFilename}`;
  try {
    await Storage.vault.put(key, file, {
      bucket,
      region,
      level: 'public',
      customPrefix,
      contentType: file.type
    })
    return {
      key
    }
  } catch (error) {
    console.log(error)
    throw new Error(`Failed to upload ${file} to storage`)
  }
}

const getFileURL = async (key: string): Promise<string> => {
  const fileUrl: any = await Storage.vault.get(key, {
    bucket: config.s3.transcodedMedia.BUCKET,
    region: config.s3.transcodedMedia.REGION,
    level: 'public',
    customPrefix
  })
  return fileUrl
}

const deleteFile = async (key: string): Promise<string> => {
  const fileUrl: any = await Storage.vault.remove(key, {
    bucket: config.s3.transcodedMedia.BUCKET,
    region: config.s3.transcodedMedia.REGION,
    level: 'public',
    customPrefix
  })
  return fileUrl
}

const getApartmentMediaObjects = async (
  apartmentId: string
): Promise<BucketObject[]> => {
  const objects: BucketObject[] = await Storage.vault.list(
    `apartments/${apartmentId}/`,
    {
      bucket: config.s3.transcodedMedia.BUCKET,
      region: config.s3.transcodedMedia.REGION,
      level: 'public',
      customPrefix
    }
  )
  // Filter out /thumb/ files — those are auto-generated thumbnails, not real photos
  return objects.filter((object) => !object.key.includes('/thumb/'))
}

const appendObjectListWithUrl = async (objects: BucketObject[]) => {
  const objectsWithUrl = await asyncReturnEach(
    objects,
    async (object: BucketObject) => {
      const url = await getFileURL(object.key)
      return {
        ...object,
        url
      }
    }
  )
  return objectsWithUrl
}

const getAllMediaObjects = async (
  apartments: Apartment[]
): Promise<ApartmentsBucketList> => {
  let allMediaByApartmentId: ApartmentsBucketList = []
  await asyncForEach(
    apartments,
    async ({ apartmentId, media, metadata }: Apartment) => {
      if (metadata.media) {
        const apartmentObjects: BucketObject[] = (
          await Storage.vault.list(
            `apartments/${apartmentId}/`,
            {
              bucket: config.s3.transcodedMedia.BUCKET,
              region: config.s3.transcodedMedia.REGION,
              level: 'public',
              customPrefix
            }
          )
        ).filter((object) => !object.key.includes('/thumb/'))
        let sortedApartmentObjects = apartmentObjects
        // The listing in the database decides what is shown, also when it is empty
        if (Array.isArray(media) && apartmentObjects) {
          sortedApartmentObjects = media
            .map((mediaItem) => {
              return (
                apartmentObjects.find(
                  (apartmentObject) => apartmentObject.key === mediaItem.key
                ) || ({} as BucketObject)
              )
            })
            .filter((file) => Boolean(file.key))
        }

        const objectsWithUrl = await appendObjectListWithUrl(
          sortedApartmentObjects
        )
        allMediaByApartmentId = [
          ...allMediaByApartmentId,
          [apartmentId, objectsWithUrl]
        ]
      }
    }
  )
  return allMediaByApartmentId
}

const uploadFileUsingPreSignedURL = async (preSignedURL: string, file: any) => {
  const responseData: any = await fetch(preSignedURL, {
    method: 'put',
    body: file
  })

  return responseData
}

export {
  uploadApartmentImageFileToS3,
  uploadFileToS3,
  getFileURL,
  deleteFile,
  getApartmentMediaObjects,
  getAllMediaObjects,
  uploadFileUsingPreSignedURL
}
